/**
 * Firebase client SDK initialisation.
 *
 * Config is read from VITE_FIREBASE_* env vars (Vite exposes only VITE_ prefixed vars).
 * Required vars (must be in .env.local / Vercel env):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 *
 * Optional (local emulator):
 *   VITE_FIREBASE_AUTH_EMULATOR_HOST  e.g. "localhost:9099"
 *
 * Only one Firebase app is initialised (singleton via getApps() guard).
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  signOut,
  onIdTokenChanged,
  type User,
} from 'firebase/auth';

// Real config when VITE_FIREBASE_* env vars are present (Vercel app deploy,
// local .env.local). When absent — Storybook build / preview — fall back to an
// inert but structurally-valid config so getAuth() does NOT throw
// `auth/invalid-api-key` at module load (which crashed the whole Storybook
// preview bundle). Firebase validates apiKey presence synchronously but makes
// no network call, so a dummy key keeps the module loadable. Stories never
// actually sign in (they inject a mock via AuthContext.Provider), and the real
// app deploy always has real env vars.
const hasRealFirebaseConfig = Boolean(import.meta.env['VITE_FIREBASE_API_KEY']);

const firebaseConfig = hasRealFirebaseConfig
  ? {
      apiKey: import.meta.env['VITE_FIREBASE_API_KEY'] as string,
      authDomain: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'] as string,
      projectId: import.meta.env['VITE_FIREBASE_PROJECT_ID'] as string,
      storageBucket: import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'] as string,
      messagingSenderId: import.meta.env['VITE_FIREBASE_MESSAGING_SENDER_ID'] as string,
      appId: import.meta.env['VITE_FIREBASE_APP_ID'] as string,
    }
  : {
      apiKey: 'AIzaSyStorybookInertKeyNoNetworkNoProject0',
      authDomain: 'storybook-inert.firebaseapp.com',
      projectId: 'storybook-inert',
      storageBucket: 'storybook-inert.appspot.com',
      messagingSenderId: '000000000000',
      appId: '1:000000000000:web:0000000000000000000000',
    };

// Singleton guard — Vite HMR can re-execute this module
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

export const auth = getAuth(app);

// Connect to emulator in dev when VITE_FIREBASE_AUTH_EMULATOR_HOST is set
const emulatorHost = import.meta.env['VITE_FIREBASE_AUTH_EMULATOR_HOST'] as string | undefined;
if (emulatorHost && import.meta.env.DEV) {
  connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: false });
}

// Google provider (reuse instance)
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// ── Auth helpers ──────────────────────────────────────────────────────────────

export async function firebaseSignInEmail(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function firebaseSignInGoogle() {
  return signInWithPopup(auth, googleProvider);
}

export async function firebaseSignInWithCustomToken(token: string) {
  return signInWithCustomToken(auth, token);
}

export async function firebaseSignOut() {
  return signOut(auth);
}

/**
 * Get the current user's ID token.
 * Returns null if no user is signed in.
 * Pass forceRefresh=true to bypass the 1-hour cache (e.g. after custom claims update).
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export { onIdTokenChanged, type User };
